import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { fmtMoney, fmtSigned, fmtUnits } from "../format";
import type { IntentSummary, UploadRole, UploadSpec, UploadTemplate, UploadView } from "../types";
import { LineChart } from "./charts";

const signedMoney = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${fmtMoney(Math.abs(v))}`;
const ROLES: { key: keyof UploadSpec["columns"]; label: string; long?: boolean }[] = [
  { key: "sku", label: "SKU" }, { key: "site", label: "Site" }, { key: "customer", label: "Customer" },
  { key: "date", label: "Period", long: true }, { key: "value", label: "Quantity", long: true },
];
const SOURCE_LABEL = { template: "Saved template", model: "Mapped by the model", rules: "Mapped by rules" };

/**
 * Mass upload: a spreadsheet replaces one forecast category, in place, for the parts, sites,
 * customers and months it covers. The server parses the file and proposes the mapping; here
 * the planner checks it, maps names that didn't match, and stages the result as a card.
 */
export default function Upload({ boardId, scenario, onClose, onStaged }: {
  boardId: string; scenario: string; onClose: () => void; onStaged: (intent: IntentSummary) => void;
}) {
  const [view, setView] = useState<UploadView | null>(null);
  const [spec, setSpec] = useState<UploadSpec | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [templates, setTemplates] = useState<UploadTemplate[]>([]);
  const [saveTemplate, setSaveTemplate] = useState(true);
  const [templateName, setTemplateName] = useState("");
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => { api.uploadTemplates(boardId).then((r) => setTemplates(r.templates)).catch(() => undefined); }, [boardId]);

  const open = async (file: File) => {
    setBusy("Reading the file…");
    setError("");
    try {
      const v = await api.openUpload(boardId, file);
      setView(v);
      setSpec(v.spec);
      setTemplateName(file.name.replace(/\.[^.]+$/, ""));
    } catch (e) { setError((e as Error).message); } finally { setBusy(""); }
  };

  // Re-apply the mapping shortly after each change; the server recomputes the whole preview.
  const change = (next: UploadSpec) => {
    setSpec(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      if (!view) return;
      setBusy("Applying…");
      try {
        const v = await api.updateUpload(boardId, view.upload_id, next);
        setView(v);
        setSpec(v.spec);
        setError("");
      } catch (e) { setError((e as Error).message); } finally { setBusy(""); }
    }, 350);
  };

  const close = () => {
    if (view) api.discardUpload(boardId, view.upload_id).catch(() => undefined);
    onClose();
  };

  const stage = async () => {
    if (!view) return;
    setBusy("Staging…");
    try {
      const r = await api.stageUpload(boardId, view.upload_id, saveTemplate, templateName);
      onStaged(r.intent);
    } catch (e) { setError((e as Error).message); setBusy(""); }
  };

  return (
    <div className="backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal upload" role="dialog" aria-label="Upload a forecast">
        <div className="row">
          <h2 className="grow">Upload a forecast{view ? `: ${view.filename}` : ""}</h2>
          {busy && <span className="row small muted"><span className="spinner" /> {busy}</span>}
          <button className="btn ghost sm" onClick={close}>Close</button>
        </div>
        {error && <div className="callout bad small">{error}</div>}
        {!view || !spec ? (
          <Picker onFile={open} templates={templates} boardId={boardId}
                  onTemplatesChange={setTemplates} disabled={!!busy} />
        ) : (
          <div className="upload-body">
            <Mapping view={view} spec={spec} onChange={change} />
            <Result view={view} spec={spec} onChange={change} />
          </div>
        )}
        {view && spec && (
          <div className="row upload-foot">
            <label className="row small"><input type="checkbox" checked={saveTemplate}
                                                onChange={(e) => setSaveTemplate(e.target.checked)} /> Remember this layout as</label>
            <input className="input" style={{ width: 220 }} value={templateName} disabled={!saveTemplate}
                   onChange={(e) => setTemplateName(e.target.value)} aria-label="Template name" />
            <span className="grow" />
            <span className="small muted">Staged changes go to <b>{scenario}</b> in {Math.round(view.push_delay_seconds / 60)} min unless you push or discard them.</span>
            <button className="btn primary" disabled={!!busy || !view.summary || !!blocking(view)} onClick={stage}
                    title={blocking(view) || undefined}>Stage</button>
          </div>
        )}
      </div>
    </div>
  );
}

function blocking(v: UploadView): string {
  const e = v.errors.find((x) => /^(Choose|Pick)|no header/.test(x));
  if (e) return e;
  const open = Object.values(v.unmatched).flat().length;
  return open ? `${open} name(s) don't match Kinaxis yet: map or skip them.` : "";
}

function Picker({ onFile, templates, boardId, onTemplatesChange, disabled }: {
  onFile: (f: File) => void; templates: UploadTemplate[]; boardId: string;
  onTemplatesChange: (t: UploadTemplate[]) => void; disabled: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <label className={`dropzone${over ? " over" : ""}`}
             onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
             onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}>
        <input type="file" accept=".xlsx,.xlsm,.csv" disabled={disabled} hidden
               onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
        <b>Drop an Excel or CSV file, or click to choose one</b>
        <span className="small muted">Any layout: SKU, SKU-site, SKU-customer or full detail; months down or across; weeks, months or quarters.
          The file replaces one forecast category for what it covers. It is read, then discarded.</span>
      </label>
      {templates.length > 0 && (
        <div>
          <div className="section-title">Layouts this board remembers</div>
          <table className="data compact">
            <tbody>{templates.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td><td className="muted">{t.spec.category}</td>
                <td className="muted small">{t.updated_by}</td>
                <td className="num"><button className="btn ghost sm" onClick={async () => {
                  await api.deleteUploadTemplate(boardId, t.id);
                  onTemplatesChange(templates.filter((x) => x.id !== t.id));
                }}>Forget</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Mapping({ view, spec, onChange }: { view: UploadView; spec: UploadSpec; onChange: (s: UploadSpec) => void }) {
  const set = <K extends keyof UploadSpec>(k: K, v: UploadSpec[K]) => onChange({ ...spec, [k]: v });
  const col = (k: keyof UploadSpec["columns"], v: string) => onChange({ ...spec, columns: { ...spec.columns, [k]: v || null } });
  const used = new Set(Object.values(spec.columns).filter(Boolean));
  return (
    <div className="upload-col">
      <div className="small"><span className="pill">{SOURCE_LABEL[view.source]}</span></div>
      {view.notes.map((n) => <div key={n} className="small ink2">• {n}</div>)}
      <div className="grid-2">
        <label className="field"><span>Sheet</span>
          <select className="input" value={spec.sheet} onChange={(e) => set("sheet", e.target.value)}>
            {view.sheets.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.rows} rows)</option>)}
          </select></label>
        <label className="field"><span>Header row</span>
          <input className="input" type="number" min={1} value={spec.header_row}
                 onChange={(e) => set("header_row", Number(e.target.value) || 1)} /></label>
      </div>
      <div className="field"><span>Periods are</span>
        <div className="row">
          <label className="row small"><input type="radio" checked={spec.layout === "long"} onChange={() => set("layout", "long")} /> one per row</label>
          <label className="row small"><input type="radio" checked={spec.layout === "wide"} onChange={() => set("layout", "wide")} /> across, as columns</label>
        </div></div>
      <div className="grid-2">
        {ROLES.filter((r) => !r.long || spec.layout === "long").map((r) => (
          <label className="field" key={r.key}><span>{r.label}{r.key === "site" || r.key === "customer" ? " (optional)" : ""}</span>
            <select className="input" value={spec.columns[r.key] ?? ""} onChange={(e) => col(r.key, e.target.value)}>
              <option value="">{r.key === "site" || r.key === "customer" ? "Not in the file (spread)" : "—"}</option>
              {view.headers.map((h) => <option key={h} value={h}>{h}</option>)}
            </select></label>
        ))}
      </div>
      {spec.layout === "wide" && (
        <div className="field"><span>Period columns</span>
          <div className="chip-row">{view.headers.filter((h) => !used.has(h)).map((h) => {
            const on = spec.period_columns.includes(h);
            return <button key={h} type="button" className={`pick${on ? " on" : ""}`}
                           onClick={() => set("period_columns", on ? spec.period_columns.filter((x) => x !== h) : [...spec.period_columns, h])}>{h}</button>;
          })}</div></div>
      )}
      <div className="grid-2">
        <label className="field"><span>Replaces category</span>
          <select className="input" value={spec.category} onChange={(e) => set("category", e.target.value)}>
            <option value="">Pick…</option>
            {view.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></label>
        <label className="field"><span>Period grain</span>
          <select className="input" value={spec.grain} onChange={(e) => set("grain", e.target.value as UploadSpec["grain"])}>
            {["auto", "month", "quarter", "week", "day"].map((g) => <option key={g} value={g}>{g}</option>)}
          </select></label>
        <label className="field"><span>Multiply values by</span>
          <input className="input" type="number" value={spec.scale} onChange={(e) => set("scale", Number(e.target.value) || 1)} /></label>
        <label className="field"><span>Skip rows matching</span>
          <input className="input" value={spec.drop_rows_matching} onChange={(e) => set("drop_rows_matching", e.target.value)} /></label>
        <label className="field"><span>Blank months in range</span>
          <select className="input" value={spec.gaps} onChange={(e) => set("gaps", e.target.value as UploadSpec["gaps"])}>
            <option value="clear">Clear to 0 (full replace)</option>
            <option value="keep">Keep what's there</option>
          </select></label>
        <label className="field"><span>Spread to site × customer by</span>
          <select className="input" value={spec.spread} onChange={(e) => set("spread", e.target.value as UploadSpec["spread"])}>
            <option value="category">The category's current mix</option>
            <option value="consensus">The consensus mix</option>
            <option value="even">Evenly</option>
          </select></label>
      </div>
      <div className="section-title">File (first rows)</div>
      <div className="grid-scroll">
        <table className="data compact">
          <tbody>{view.grid.map((row, i) => (
            <tr key={i} className={i + 1 === spec.header_row ? "hdr" : undefined}>
              <td className="muted">{i + 1}</td>{row.map((c, j) => <td key={j}>{c}</td>)}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Result({ view, spec, onChange }: { view: UploadView; spec: UploadSpec; onChange: (s: UploadSpec) => void }) {
  const s = view.summary;
  const mapName = (role: UploadRole, raw: string, to: string) =>
    onChange({ ...spec, value_map: { ...spec.value_map, [role]: { ...spec.value_map[role], [raw]: to } } });
  const unmatched = (Object.entries(view.unmatched) as [UploadRole, { raw: string; rows: number; candidates: string[] }[]][])
    .flatMap(([role, list]) => list.map((u) => ({ role, ...u })));
  return (
    <div className="upload-col">
      {view.errors.length > 0 && (
        <div className="callout warn small">{view.errors.map((e) => <div key={e}>{e}</div>)}</div>
      )}
      {unmatched.length > 0 && (
        <div className="callout warn">
          <div className="small"><b>Names that don't match Kinaxis</b>: map each one or skip its rows.</div>
          {unmatched.map((u) => (
            <div className="row small" key={`${u.role}:${u.raw}`}>
              <span className="mono grow">{u.raw} <span className="muted">({u.role}, {u.rows} rows)</span></span>
              <select className="input" style={{ width: 200 }} value="" onChange={(e) => mapName(u.role, u.raw, e.target.value)}>
                <option value="">Choose…</option>
                {u.candidates.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__skip__">Skip these rows</option>
              </select>
            </div>
          ))}
        </div>
      )}
      <div className="small muted">
        {view.rows_read} values read, {view.rows_used} used{view.issue_count ? `, ${view.issue_count} rows with problems` : ""}
        {view.window ? ` · window ${view.window[0]} to ${view.window[1]}` : ""}
      </div>
      {view.issues.length > 0 && (
        <details className="small"><summary>Rows with problems</summary>
          {view.issues.map((i) => <div key={`${i.row}:${i.problem}`}>Row {i.row}: {i.problem}</div>)}
        </details>
      )}
      {s && (
        <>
          <div className="upload-kpis">
            <div><b>{fmtUnits(s.counts.update)}</b><span>updated</span></div>
            <div><b>{fmtUnits(s.counts.insert)}</b><span>added</span></div>
            <div className={s.counts.clear ? "warn" : undefined}><b>{fmtUnits(s.counts.clear)}</b>
              <span>cleared to 0{s.cleared_qty ? ` (${fmtUnits(s.cleared_qty)} units)` : ""}</span></div>
            <div><b>{fmtSigned(s.totals.delta)}</b><span>units · {signedMoney(s.totals.delta_value)}</span></div>
          </div>
          <LineChart height={150} labels={s.months.map((m) => m.month)} highlight={s.months.map((m) => m.changed)} series={[
            { name: "Before", color: "var(--series-base)", dashed: true, values: s.months.map((m) => m.before) },
            { name: "After", color: "var(--series-target)", values: s.months.map((m) => m.after) },
          ]} />
          <table className="data compact">
            <thead><tr><th>SKU</th><th className="num">Before</th><th className="num">After</th><th className="num">Δ</th><th className="num">$</th></tr></thead>
            <tbody>{s.by_sku.slice(0, 12).map((r) => (
              <tr key={r.sku}><td className="mono">{r.sku}</td><td className="num">{fmtUnits(r.before)}</td>
                <td className="num">{fmtUnits(r.after)}</td><td className="num">{fmtSigned(r.delta)}</td>
                <td className="num">{signedMoney(r.delta_value)}</td></tr>
            ))}</tbody>
          </table>
          {s.by_sku.length > 12 && <div className="small muted">…and {s.by_sku.length - 12} more SKUs</div>}
        </>
      )}
    </div>
  );
}
